/**
 * RequireSuperAdmin -- Phase 3 (CON-01) auth guard for /console/* routes.
 *
 * Mirrors RequireAuth's children-or-Outlet API:
 *
 * 1. Single-route wrapper (children-based):
 *      <Route element={<RequireSuperAdmin><X /></RequireSuperAdmin>} />
 *
 * 2. Layout-route element (group-based, falls back to <Outlet/>):
 *      <Route element={<RequireSuperAdmin />}>
 *        <Route path="/console" element={<DashboardPage />} />
 *        <Route path="/console/news-articles" ... />
 *      </Route>
 *
 * Four states (exhaustive — see UI-SPEC):
 *  - isLoading -> centered LoadingSpinner; no premature redirect
 *  - !isLoggedIn -> Navigate to /login?redirect=<encoded current> (preserves
 *    the deep-link so login can hand the user back to the page they wanted)
 *  - logged-in but isSuperAdmin !== true -> Navigate to /account, fire
 *    sonner toast with the exact UI-SPEC copy: "Console requires
 *    super-admin access." (toast fires once per non-super-admin entry —
 *    useRef guards against re-render double-firing)
 *  - logged-in super-admin -> render children ?? <Outlet/>
 *
 * The `!== true` check (not `!user.isSuperAdmin`) is deliberate: the User
 * type's isSuperAdmin field is optional. Pre-Phase-3 cached sessions may
 * have `undefined`, which must be treated identically to `false`. `!== true`
 * makes that semantic explicit.
 *
 * Companion to RequireAuth (auth-required browser routes) and
 * RedirectIfAuthed (auth-page anti-redirect). All three live in
 * src/core/auth/ and share the LoadingSpinner pattern.
 */

import { type ReactNode, useEffect, useRef } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '@/lib/auth'
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner'

interface RequireSuperAdminProps {
  children?: ReactNode
}

export function RequireSuperAdmin({ children }: RequireSuperAdminProps) {
  const { isLoading, isLoggedIn, user } = useAuth()
  const location = useLocation()
  const toastFiredRef = useRef(false)

  // Toast must fire ONCE per non-super-admin entry. Using a ref instead of
  // state prevents the toast from re-firing on every parent re-render and
  // also avoids a redundant render cycle. Reset to false when the user
  // becomes a super-admin or logs out so the next denied entry produces a
  // fresh toast.
  useEffect(() => {
    if (
      !isLoading &&
      isLoggedIn &&
      user &&
      user.isSuperAdmin !== true &&
      !toastFiredRef.current
    ) {
      toast.error('Console requires super-admin access.')
      toastFiredRef.current = true
    }
    if (isLoading || !isLoggedIn || user?.isSuperAdmin === true) {
      toastFiredRef.current = false
    }
  }, [isLoading, isLoggedIn, user])

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-10rem)] items-center justify-center bg-ue-bg-darkest">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isLoggedIn) {
    const redirect = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?redirect=${redirect}`} replace />
  }

  if (user?.isSuperAdmin !== true) {
    return <Navigate to="/account" replace />
  }

  return <>{children ?? <Outlet />}</>
}
